<template>
    <div>
        <page-header>Invites</page-header>

        <vx-alert v-if="acceptedInvitation" class="mb-4" color="success" :closeable="false" shadow>
            You have successfully joined the <strong>{{ acceptedInvitation.team.name }}</strong> team!
        </vx-alert>

        <vx-alert
            v-else-if="linkedInvitation"
            class="mb-4"
            color="primary"
            :closeable="false"
            shadow
            data-test="linked-invitation"
        >
            You're invited to join the <strong>{{ linkedInvitation.team.name }}</strong> team.

            <template v-slot:right>
                <vx-button
                    @click.prevent="accept"
                    data-test="accept-button"
                    size="sm"
                    color="primary"
                    :disabled="loading"
                    :loading="accepting"
                >
                    <template v-if="acceptsWithSso(linkedInvitation)">
                        Log In with SSO <font-awesome-icon widthAuto :icon="faRightToBracket" />
                    </template>
                    <template v-else>Accept <font-awesome-icon widthAuto :icon="faCheck" /></template>
                </vx-button>
            </template>
        </vx-alert>

        <vx-alert
            v-else-if="linkUnusable"
            class="mb-4"
            color="danger"
            :closeable="false"
            shadow
            data-test="invitation-unusable"
        >
            This invitation link can't be used. If you've already joined the team, there's nothing more to do, and if it
            was sent to another Voxie account of yours, log in to that account and open it again. Otherwise it has
            expired or been canceled or declined, so ask whoever invited you to send it again.
        </vx-alert>

        <vx-alert
            v-if="invitations.some((invitation) => invitation.id !== linkedInvitation?.id)"
            class="mb-4"
            color="info"
            :closeable="false"
            data-test="invitations-notice"
        >
            Accept an invitation from the link in its email. If you can't find the email or its link doesn't let you
            accept, ask whoever invited you to send it again.
        </vx-alert>

        <page-loading v-if="loading && !invitations.length"></page-loading>

        <vx-box
            color="info"
            v-else-if="!loading && !invitations.length && !linkedInvitation"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            There are no pending invitations.
        </vx-box>

        <vx-table v-else-if="invitations.length" class="xl:grid-cols-3-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Team</div>
            </vx-table-row>

            <vx-table-row v-for="(invitation, index) in invitations" :key="index" data-test="invite-row">
                <vx-table-row-title class="xl:col-span-2!" data-test="team-name">
                    {{ invitation.team.name }}
                    <vx-badge
                        v-if="invitation.team.require_sso"
                        size="xs"
                        color="sky"
                        class="ml-2"
                        data-test="requires-sso-badge"
                    >
                        Requires SSO
                    </vx-badge>
                </vx-table-row-title>

                <vx-table-row-buttons>
                    <vx-dropdown placement="right">
                        <vx-button
                            data-test="dropdown"
                            aria-label="Invitation actions"
                            type="button"
                            color="muted"
                            outline
                            class="w-[45px]"
                        >
                            <font-awesome-icon widthAuto :icon="faEllipsis" />
                        </vx-button>

                        <template v-slot:items>
                            <vx-dropdown-item data-test="reject-button" @click="reject(invitation)">
                                Reject
                                <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                </vx-table-row-buttons>
            </vx-table-row>
        </vx-table>
    </div>
</template>

<script setup>
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import axios from '~/services/axios';
import {
    VxAlert,
    VxBadge,
    VxBox,
    VxButton,
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowButtons,
    VxDropdown,
    VxDropdownItem,
    VxSwal,
} from '@voxie/frontend-components';
import { ref } from 'vue';
import { useUrlSearchParams } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faEllipsis, faRightToBracket, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useToasted } from '~/composables/useToasted';
import { throwSwalRequestError } from '~/utils/request';
import { stringifyQueryString } from '~/utils/string';

const toasted = useToasted();
const queryParams = useUrlSearchParams();
const linkedToken =
    typeof queryParams.invitation === 'string' && /^[A-Za-z0-9]{40}$/.test(queryParams.invitation)
        ? queryParams.invitation
        : undefined;
const loading = ref(false);
const accepting = ref(false);
const acceptedInvitation = ref();
const linkedInvitation = ref();
const linkUnusable = ref(false);
const invitations = ref([]);
const rejectedIds = new Set();

const getPendingInvitations = () => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    axios
        .get('/settings/invitations/pending')
        .then((response) => {
            invitations.value = response.data;
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            loading.value = false;
        });
};

const getLinkedInvitation = () => {
    axios
        .get(`/settings/invitations/${linkedToken}`)
        .then((response) => {
            if (rejectedIds.has(response.data.id)) {
                queryParams.invitation = undefined;
                return;
            }

            linkedInvitation.value = response.data;
        })
        .catch((e) => {
            if (e.response?.status === 404) {
                linkUnusable.value = true;
                return;
            }

            console.error(e);
            toasted.global.platform_error();
        });
};

const reject = (invitation) => {
    acceptedInvitation.value = undefined;
    VxSwal.confirmation('Reject Invite', 'Are you sure? There is no undo.', 'danger', () =>
        axios
            .post(`/settings/invitations/${invitation.id}/reject`)
            .then(() => {
                rejectedIds.add(invitation.id);

                if (invitation.id === linkedInvitation.value?.id) {
                    linkedInvitation.value = undefined;
                    queryParams.invitation = undefined;
                }

                getPendingInvitations();
            })
            .catch(throwSwalRequestError)
    );
};

const acceptsWithSso = (invitation) => Boolean(invitation.locked && invitation.sso_login_url);

const accept = () => {
    const invitation = linkedInvitation.value;

    if (acceptsWithSso(invitation)) {
        // link=1: the IdP identity must resolve to this signed-in account; the ACS accepts the invitation
        window.location.assign(
            `${invitation.sso_login_url}?${stringifyQueryString({
                invitation: linkedToken,
                link: 1,
                intended: '/settings#/invites',
            })}`
        );
        return;
    }

    accepting.value = true;
    axios
        .post(`/settings/invitations/${linkedToken}/accept`)
        .then(() => {
            Bus.$emit('updateTeams');
            acceptedInvitation.value = invitation;
            linkedInvitation.value = undefined;
            queryParams.invitation = undefined;

            getPendingInvitations();
        })
        .catch((e) => {
            if (e.response?.status === 404) {
                linkedInvitation.value = undefined;
                linkUnusable.value = true;
                return;
            }

            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            accepting.value = false;
        });
};

if (linkedToken) {
    getLinkedInvitation();
} else if (queryParams.invitation) {
    linkUnusable.value = true;
}

getPendingInvitations();
</script>
